import { useEffect, useRef, useState } from "react";
import { Gift, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function ComplimentaryPlanCard() {
  const { user } = useAuth();
  const [form, setForm] = useState({ email: "", plan: "premium", reason: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const ref = useRef(null);
  useEffect(() => { if (window.location.hash === "#complimentary-plan" && ref.current) ref.current.scrollIntoView({ behavior: "smooth", block: "start" }); }, [user]);
  if (!user?.is_admin) return null;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/users/complimentary-plan", form);
      setResult(data);
      toast.success(`${data.email}: ${data.before} → ${data.plan}`);
    } catch (err) { toast.error(apiError(err, "Could not update plan")); }
    finally { setBusy(false); }
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <section id="complimentary-plan" ref={ref} className="apx-card p-5 space-y-3 scroll-mt-24" data-testid="complimentary-plan-card">
      <h2 className="font-display font-semibold text-lg flex items-center gap-2"><Gift className="w-4 h-4 text-cyan-300" /> Complimentary access (owner tools)</h2>
      <p className="text-xs text-slate-400 leading-relaxed">Grants or revokes a plan on one account that has no Stripe history — a non-admin account (e.g. store reviewer) or your own owner account; other admin accounts and paying customers are refused (manage them in Stripe). Every change is written to the admin audit log.</p>
      <form onSubmit={submit} className="grid gap-2 sm:grid-cols-[1fr_auto_1fr_auto]">
        <input required type="email" value={form.email} onChange={set("email")} placeholder="account email" data-testid="comp-plan-email" className="apx-ring px-3 py-2 rounded-lg bg-slate-900/80 border border-slate-800 outline-none text-sm" />
        <select value={form.plan} onChange={set("plan")} data-testid="comp-plan-plan" className="apx-ring px-3 py-2 rounded-lg bg-slate-900/80 border border-slate-800 outline-none text-sm">
          <option value="premium">Premium</option><option value="pro">Pro</option><option value="free">Free (revoke)</option>
        </select>
        <input required minLength={5} value={form.reason} onChange={set("reason")} placeholder="reason (audit log)" data-testid="comp-plan-reason" className="apx-ring px-3 py-2 rounded-lg bg-slate-900/80 border border-slate-800 outline-none text-sm" />
        <button type="submit" disabled={busy} data-testid="comp-plan-submit" className="btn-primary rounded-lg px-4 py-2 text-sm inline-flex items-center gap-2">{busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Apply</button>
      </form>
      {result && <div className="text-xs text-emerald-300" data-testid="comp-plan-result">{result.email}: {result.before} → {result.plan}{result.until ? ` until ${result.until.slice(0, 10)}` : ""}</div>}
    </section>
  );
}
